import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/src/modules/components/Button';
import { Icons } from '@/src/modules/components/Icons';
import { StepProps, ApiTemplate } from '@/src/types';
import { cn } from '@/src/infrastructure/lib/utils';
import { motion, AnimatePresence } from 'motion/react';
import { getTemplates } from '@/src/infrastructure/lib/api';
import { JsonTemplateRenderer } from '@/src/modules/renderer/JsonTemplateRenderer';
import { TemplateCardPreview } from '@/src/modules/renderer/TemplateCardPreview';
import type { ApiInvitation } from '@/src/types';
import { CinematicWeddingExperience } from '@/src/modules/experiences/wedding/CinematicWeddingExperience';
import { TemplateRenderer, registry } from '@/src/modules/templates';
import { SceneExperienceRenderer } from '@/src/modules/experiences/scene/SceneExperienceRenderer';
import { resolveExperienceManifest } from '@/src/modules/pages/ExperiencePreview';
import { trackEvent } from '@/src/infrastructure/lib/analytics';
import { trackTemplateChange } from '@/src/infrastructure/lib/behaviour-analytics';
import { switchTemplate, type SwitchReport } from '@/src/modules/wizard/schema/switch-template';
import { SwitchTemplateDialog } from './SwitchTemplateDialog';

/**
 * CinematicWedding — immersive multi-section wedding experience template.
 * DB id: 'tpl_cinematic_wedding_001' (migration 20260606000000_fix_cinematic_wedding_id_add_lagna_patrika)
 * Frontend registry: CinematicWedding/index.tsx
 *
 * NOTE: The old id 'tpl_wedding_classic_001' / 'seed-wedding-classic-coral-sky-1' is
 * retired. This is the correct canonical id going forward.
 */
const CINEMATIC_WEDDING_TEMPLATE: ApiTemplate = {
  id: 'tpl_cinematic_wedding_001',
  slug: 'cinematic-wedding-experience',
  name: 'Cinematic Wedding Experience',
  occasionType: 'WEDDING',
  previewImageUrl: 'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=720&q=80',
  isPremium: true,
  isActive: true,
  description: 'Immersive wedding microsite with couple storytelling, family tree, multiple events with maps, pre-wedding media gallery, and RSVP.',
  tags: ['cinematic', 'wedding', 'storytelling', 'premium', 'experience'],
  capabilities: {
    supportsRSVP: true,
    supportsGallery: true,
    supportsMap: true,
    supportsMultipleEvents: true,
    experienceRenderer: 'cinematic-wedding',
  },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

/**
 * Dev21 — a code-rendered React experience template. It is not stored as a
 * JSON schema, so it is offered here as a fixed catalogue entry; rendering is
 * delegated to the frontend template `registry` by `id`.
 */
const ROYAL_GANESH_INTERACTIVE_TEMPLATE: ApiTemplate = {
  id: 'royal-ganesh-interactive',
  slug: 'royal-ganesh-interactive',
  name: 'Royal Ganesh — Interactive',
  occasionType: 'WEDDING',
  previewImageUrl: '/assets/royal-ganesh-interactive/extracted/logo/logo.png',
  isPremium: true,
  isActive: true,
  description:
    'A premium, animated, interactive wedding invitation featuring Ganesha, custom floral motifs, and a countdown.',
  tags: ['wedding', 'ganesh', 'interactive', 'animated', 'premium', 'royal', 'blue'],
  capabilities: { experienceRenderer: 'royal-ganesh-interactive' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const DEV21_WEDDING_TEMPLATE: ApiTemplate = {
  id: 'dev21',
  slug: 'dev21-modern-premium',
  name: 'Dev21 — Modern Premium',
  occasionType: 'WEDDING',
  previewImageUrl: null,
  isPremium: true,
  isActive: true,
  description:
    'A fresh, cinematic single-page wedding invitation: parallax hero, animated story timeline, masonry gallery, teaser video, family tree, schedule, live map and a validated RSVP.',
  tags: ['modern', 'premium', 'animated', 'glassmorphism', 'responsive'],
  capabilities: { experienceRenderer: 'dev21' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

/**
 * LagnaPatrikaInteractive — a code-rendered React experience template.
 * Features: traditional marigold garlands, floating rose petals, 3D tilt effects,
 * and built-in English/Marathi translation toggles.
 */
const LAGNA_PATRIKA_INTERACTIVE_TEMPLATE: ApiTemplate = {
  id: 'lagna-patrika-interactive',
  slug: 'lagna-patrika-interactive',
  name: 'Lagna Patrika - Interactive',
  occasionType: 'WEDDING',
  previewImageUrl: 'https://images.unsplash.com/photo-1542451313056-b7c8e626645f?auto=format&fit=crop&w=720&q=80',
  isPremium: true,
  isActive: true,
  description:
    'An elegant, interactive Lagna Patrika featuring Marathi/English bilingual toggles, traditional mandap designs, and responsive layouts.',
  tags: ['interactive', 'traditional', 'marathi', 'bilingual', 'animated', 'premium'],
  capabilities: { experienceRenderer: 'lagna-patrika-interactive' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

/**
 * TraditionalWeddingInteractive — rich interactive wedding invitation.
 * Features: couple intro, multi-event timeline, expandable family tree,
 * family invitation cards, and integrated RSVP.
 */
const VIDEO_ROYAL_01_TEMPLATE: ApiTemplate = {
  id: 'video-01-royal-gold',
  slug: 'video-01-royal-gold',
  name: 'Royal Gold Video — Animated Ceremonies',
  occasionType: 'WEDDING',
  previewImageUrl: '/assets/templates/video-01-royal/board_frame.png',
  isPremium: true,
  isActive: true,
  description: 'A breathtaking, cinematic Indian wedding invitation powered by real-time video backgrounds, floral board framing, ceremony caricature art, and dynamic typography.',
  tags: ['video', 'royal', 'gold', 'cinematic', 'animated', 'premium', 'scroll-experience', 'marathi', 'wedding', 'haldi', 'mehendi', 'sangeet', 'reception', 'vocal', 'wow'],
  capabilities: { experienceRenderer: 'video-01-royal-gold', supportsRSVP: true, supportsCountdown: true, supportsMusic: true },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const VIDEO_KIRTAN_DIVINE_TEMPLATE: ApiTemplate = {
  id: 'video-02-kirtan-divine',
  slug: 'video-02-kirtan-divine',
  name: 'Radha Krishna Kirtan — Divine Video Invitation',
  occasionType: 'WEDDING',
  previewImageUrl: '/assets/templates/video-02-kirtan-divine/ganesh_header.png',
  isPremium: true,
  isActive: true,
  description: 'A divine, cinematic Radha-Krishna Kirtan and wedding invitation featuring peacock artwork framing, Shree Ganesha header, real-time motion background, background audio, and dynamic typography.',
  tags: ['video', 'kirtan', 'krishna', 'radha', 'radha-krishna', 'devotional', 'wedding', 'marathi', 'hindi', 'peacock', 'god', 'divine-love', 'cinematic', 'scroll-experience', 'animated', 'wow'],
  capabilities: { experienceRenderer: 'video-02-kirtan-divine', supportsRSVP: true, supportsCountdown: true, supportsMusic: true },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const VIDEO_RADHA_KRISHNA_TEMPLATE: ApiTemplate = {
  id: 'video-03-radha-krishna',
  slug: 'video-03-radha-krishna',
  name: 'Radha Krishna Garden — PSD Video Invitation',
  occasionType: 'WEDDING',
  previewImageUrl: '/assets/templates/video-03-radha-krishna/mandap-forest.webp',
  isPremium: true,
  isActive: true,
  description: 'A PSD-derived Radha Krishna garden wedding invitation with cinematic scroll scenes, floral and gold particle motion overlays, dynamic ceremony details, photo showcase cards, RSVP, and map directions.',
  tags: ['video', 'radha', 'krishna', 'radha-krishna', 'garden', 'mandap', 'devotional', 'wedding', 'haldi', 'sangeet', 'reception', 'cinematic', 'scroll-experience', 'animated', 'wow'],
  capabilities: { experienceRenderer: 'video-03-radha-krishna', supportsRSVP: true, supportsCountdown: true, supportsMusic: false, supportsMap: true },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const EXP_GSAP_RADHA_KRISHNA_TEMPLATE: ApiTemplate = {
  id: 'exp-gsap-radha-krishna',
  slug: 'exp-gsap-radha-krishna',
  name: 'Radha Krishna GSAP Scroll Divine',
  occasionType: 'WEDDING',
  previewImageUrl: '/assets/templates/video-03-radha-krishna/mandap-forest.webp',
  isPremium: true,
  isActive: true,
  description: 'An opulent GSAP ScrollTrigger scroll-driven traditional Radha Krishna wedding invitation featuring 3D perspective animations, floating divine lotus and peacock feather parallax, pinned ritual scenes, and single scroll ergonomics.',
  tags: ['radha-krishna', 'gsap', 'scroll-driven', 'traditional', 'wedding', 'hindu-gods', 'divine', 'cinematic', '3d', 'scroll-experience', 'ai-native', 'krishna', 'radha', 'mandap', 'haldi', 'phere'],
  capabilities: { experience: true, aiNative: true, stylePack: 'heritage', supportsRSVP: true, experienceRenderer: 'exp-gsap-radha-krishna', supportsMap: true },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const EXP_GSAP_SHIVA_PARVATI_TEMPLATE: ApiTemplate = {
  id: 'exp-gsap-shiva-parvati',
  slug: 'exp-gsap-shiva-parvati',
  name: 'Shiva Parvati Divine Marriage',
  occasionType: 'WEDDING',
  previewImageUrl: '/assets/templates/exp-shiva-parvati/shiva_parvati_hero_bg.jpg',
  isPremium: true,
  isActive: true,
  description: 'A majestic GSAP ScrollTrigger scroll-driven traditional Hindu gods invitation celebrating the divine marriage of Lord Shiva & Goddess Parvati on Mount Kailash with 3D perspective animations and sacred floating accents.',
  tags: ['shiva-parvati', 'gsap', 'scroll-driven', 'traditional', 'wedding', 'hindu-gods', 'divine', 'kailash', 'om-namah-shivaya', 'damru', 'trishul', 'sacred-matrimony', 'cinematic', '3d', 'scroll-experience', 'ai-native', 'shiva', 'parvati', 'mandap', 'tilak', 'phere'],
  capabilities: { experience: true, aiNative: true, stylePack: 'heritage', supportsRSVP: true, experienceRenderer: 'exp-gsap-shiva-parvati', supportsMap: true },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};


const TRADITIONAL_WEDDING_INTERACTIVE_TEMPLATE: ApiTemplate = {
  id: 'traditional-wedding-interactive',
  slug: 'traditional-wedding-interactive',
  name: 'Traditional Wedding — Interactive',
  occasionType: 'WEDDING',
  previewImageUrl:
    'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=720&q=80',
  isPremium: true,
  isActive: true,
  description:
    'Rich interactive wedding invitation: couple introduction, multi-event timeline, expandable family tree, family invitation cards, and integrated RSVP.',
  tags: ['wedding', 'traditional', 'interactive', 'family-tree', 'timeline', 'premium'],
  capabilities: { experienceRenderer: 'traditional-wedding-interactive' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

/**
 * StitchImmersiveWedding — a macOS-inspired glassmorphism wedding experience.
 * Features: parallax wallpaper, animated dock, cycling name greeter, floating glass windows.
 */
const STITCH_IMMERSIVE_WEDDING_TEMPLATE: ApiTemplate = {
  id: 'stitch-immersive-wedding',
  slug: 'stitch-immersive-wedding',
  name: 'Stitch Immersive Wedding',
  occasionType: 'WEDDING',
  previewImageUrl:
    'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=720&q=80',
  isPremium: true,
  isActive: true,
  description:
    'A macOS-inspired glassmorphism wedding invitation with immersive parallax background, animated dock navigation, and floating glass windows.',
  tags: ['glassmorphism', 'macos', 'immersive', 'animated', 'wedding', 'premium'],
  capabilities: { experienceRenderer: 'stitch-immersive-wedding' },
  version: '1.0.0',
  renderMode: 'json-renderer',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

/**
 * MarathiWeddingClassic — a beautiful traditional Marathi wedding template.
 * Features: Type B manifest-driven design, traditional motifs, animations.
 */
const MARATHI_WEDDING_CLASSIC_TEMPLATE: ApiTemplate = {
  id: 'marathi-wedding-classic',
  slug: 'marathi-wedding-classic',
  name: 'Marathi Wedding Classic',
  occasionType: 'WEDDING',
  previewImageUrl: 'https://pub-2b435ac1f79c40fbad08ae6c4e4f4143.r2.dev/type-b/harw/bg.png',
  isPremium: true,
  isActive: true,
  description: 'A beautiful traditional Marathi wedding template generated with Type B pipeline.',
  tags: ['marathi', 'traditional', 'type-b', 'premium'],
  capabilities: {
    supportsRSVP: true,
    supportsGallery: false,
    supportsMap: true,
    experienceRenderer: 'marathi-wedding-classic',
  },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

/**
 * MarathiWeddingInteractive — a bilingual Marathi wedding invitation.
 * Features: 3D parallax, falling marigold petals, glowing sways, translation.
 */
const MARATHI_WEDDING_INTERACTIVE_TEMPLATE: ApiTemplate = {
  id: 'marathi-wedding-interactive',
  slug: 'marathi-wedding-interactive',
  name: 'Marathi Wedding Interactive',
  occasionType: 'WEDDING',
  previewImageUrl: '/assets/marathi/backdrop.jpg',
  isPremium: true,
  isActive: true,
  description: 'A premium interactive Lagna Patrika featuring a Marathi/English bilingual translation toggle, gentle 3D parallax scrolling, and falling gold marigold petals.',
  tags: ['marathi', 'traditional', '3d', 'interactive', 'animated', 'premium'],
  capabilities: {
    supportsRSVP: true,
    supportsGallery: false,
    supportsMap: true,
    experienceRenderer: 'marathi-wedding-interactive',
  },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const SOUTH_INDIAN_TRADITION_TEMPLATE: ApiTemplate = {
  id: 'south-indian-tradition-experience',
  slug: 'south-indian-tradition-experience',
  name: 'South Indian Tradition — Tamil/Telugu/Kannada',
  occasionType: 'WEDDING',
  previewImageUrl: 'https://images.unsplash.com/photo-1601297561143-796741160e8a?auto=format&fit=crop&w=720&q=80',
  isPremium: true,
  isActive: true,
  description: 'A traditional South Indian wedding: temple gopuram hero, animated diyas, ceremony programme with tilak, mangala sutra, and reception.',
  tags: ['south-indian', 'tamil', 'telugu', 'kannada', 'temple', 'traditional', 'animated'],
  capabilities: {
    supportsRSVP: true,
    supportsGallery: false,
    supportsCountdown: false,
    supportsMusic: false,
    supportsMap: true,
    experienceRenderer: 'south-indian-tradition-experience',
  },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const NIKAH_ELEGANT_TEMPLATE: ApiTemplate = {
  id: 'nikah-elegant-experience',
  slug: 'nikah-elegant-experience',
  name: 'NikahElegant — Elegant Muslim Wedding',
  occasionType: 'WEDDING',
  previewImageUrl: 'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=720&q=80',
  isPremium: true,
  isActive: true,
  description: 'A refined Muslim Nikah invitation: Bismillah header, animated geometric mandala, family details, ceremony & walima events, and a clear RSVP.',
  tags: ['nikah', 'muslim', 'elegant', 'arabic', 'animated', 'gold'],
  capabilities: {
    supportsRSVP: true,
    supportsGallery: false,
    supportsCountdown: false,
    supportsMusic: false,
    supportsMap: true,
    experienceRenderer: 'nikah-elegant-experience',
  },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

/**
 * Cinematic Royal — AI-native Type-A experience template.
 * Generates personalised ceremony images via Gemini and weaves them into
 * a scroll-driven regal gold & ivory wedding experience.
 */
const EXP_CINEMATIC_ROYAL_TEMPLATE: ApiTemplate = {
  id: 'exp-cinematic-royal-v1',
  slug: 'exp-cinematic-royal-v1',
  name: 'Cinematic Royal',
  occasionType: 'WEDDING',
  previewImageUrl: '/assets/wow/gemini-wedding-hero.png',
  isPremium: true,
  isActive: false,
  description: 'Regal gold and ivory wedding experience with scroll-driven ceremony sections, interactive couple storytelling, and RSVP.',
  tags: ['cinematic', 'wedding', 'royal', 'premium', 'scroll-experience'],
  capabilities: { experience: true, aiNative: true, stylePack: 'royal', supportsRSVP: true },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const EXP_WEDDING_RAJASTHANI_ROYAL_TEMPLATE: ApiTemplate = {
  id: 'exp-wedding-rajasthani-royal-v1',
  slug: 'exp-wedding-rajasthani-royal-v1',
  name: 'Rajasthani Rajput Royal',
  occasionType: 'WEDDING',
  previewImageUrl: '/assets/wow/rajasthani_palace_bg.jpg',
  isPremium: true,
  isActive: true,
  description: 'Grand Rajasthani palace wedding experience with rich crimson, warm gold, traditional Rajput motifs, and event timeline.',
  tags: ['cinematic', 'wedding', 'rajasthani', 'rajput', 'royal', 'premium', 'scroll-experience'],
  capabilities: { experience: true, aiNative: true, stylePack: 'heritage', supportsRSVP: true },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const EXP_CINEMATIC_MINIMAL_TEMPLATE: ApiTemplate = {
  id: 'exp-cinematic-minimal-v1',
  slug: 'exp-cinematic-minimal-v1',
  name: 'Cinematic Minimal',
  occasionType: 'WEDDING',
  previewImageUrl: '/assets/marathi/backdrop.jpg',
  isPremium: true,
  isActive: false,
  description: 'Clean white and charcoal minimal wedding experience with elegant typography and subtle parallax scrolling.',
  tags: ['cinematic', 'wedding', 'minimal', 'premium', 'scroll-experience'],
  capabilities: { experience: true, aiNative: true, stylePack: 'minimal', supportsRSVP: true },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const EXP_CINEMATIC_FANTASY_TEMPLATE: ApiTemplate = {
  id: 'exp-cinematic-fantasy-v1',
  slug: 'exp-cinematic-fantasy-v1',
  name: 'Cinematic Fantasy',
  occasionType: 'WEDDING',
  previewImageUrl: '/assets/marathi/peacock.jpg',
  isPremium: true,
  isActive: false,
  description: 'Enchanted wedding experience with deep jewel tones, vibrant purple and teal hues, and scroll-driven parallax.',
  tags: ['cinematic', 'wedding', 'fantasy', 'premium', 'scroll-experience'],
  capabilities: { experience: true, aiNative: true, stylePack: 'fantasy', supportsRSVP: true },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

export const EXPERIENCE_STYLE_BY_TEMPLATE_ID: Record<string, string> = {
  'exp-cinematic-royal-v1': 'royal',
  'exp-cinematic-minimal-v1': 'minimal',
  'exp-cinematic-fantasy-v1': 'fantasy',
  'exp-wedding-rajasthani-royal-v1': 'heritage',
  'exp-gsap-radha-krishna': 'heritage',
  'exp-gsap-shiva-parvati': 'heritage',
  'exp-birthday-superhero-v1': 'midnight_glow',
  'exp-birthday-dark-knight-v1': 'modern_noir',
  'exp-birthday-wonderland-v1': 'pastel_dream',
};

const ROYAL_ENGAGEMENT_TEMPLATE: ApiTemplate = {
  id: 'royal-engagement-experience',
  slug: 'royal-engagement-experience',
  name: 'Royal Engagement — Rajput Royal',
  occasionType: 'ENGAGEMENT',
  previewImageUrl: 'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=720&q=80',
  isPremium: true,
  isActive: true,
  description: 'A regal North Indian engagement invitation: animated rose-petal hero, ring-box reveal, both families, ring ceremony and a royal celebration programme with RSVP.',
  tags: ['engagement', 'royal', 'rajput', 'punjabi', 'animated', 'premium'],
  capabilities: {
    supportsRSVP: true,
    supportsGallery: false,
    supportsCountdown: false,
    supportsMusic: true,
    supportsMap: true,
    experienceRenderer: 'royal-engagement-experience',
  },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const MODERN_FUSION_TEMPLATE: ApiTemplate = {
  id: 'modern-fusion-experience',
  slug: 'modern-fusion-experience',
  name: 'Modern Fusion — Urban Indian',
  occasionType: 'ENGAGEMENT',
  previewImageUrl: 'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=720&q=80',
  isPremium: true,
  isActive: true,
  description: 'A contemporary Indian engagement: glassmorphism hero, story timeline, events with dress codes, and RSVP with dietary preferences.',
  tags: ['modern', 'urban', 'fusion', 'minimalist', 'glassmorphism', 'engagement'],
  capabilities: {
    supportsRSVP: true,
    supportsGallery: false,
    supportsCountdown: false,
    supportsMusic: false,
    supportsMap: true,
    experienceRenderer: 'modern-fusion-experience',
  },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

/**
 * BirthdayCelebration — a code-rendered React template for birthday invitations.
 * Features: deep blue gradient, floating balloons, trio photo layout, happy members grid, activity timeline.
 * Rendering delegated to the frontend template `registry` by `id`.
 */
const BIRTHDAY_CELEBRATION_TEMPLATE: ApiTemplate = {
  id: 'birthday-celebration',
  slug: 'birthday-celebration',
  name: 'Birthday Celebration',
  occasionType: 'BIRTHDAY',
  previewImageUrl: null,
  isPremium: true,
  isActive: true,
  description:
    'Deep blue multi-section birthday invitation: floating balloons, trio photo layout, happy celebrants grid, and a vertical activity timeline.',
  tags: ['birthday', 'blue', 'animated', 'multi-section', 'celebration'],
  capabilities: { experienceRenderer: 'birthday-celebration' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

/**
 * BirthdayPremium — the new premium, story-driven birthday template.
 * Distinct id from 'birthday-celebration' (which stays frozen). Adds a
 * full-screen hero, memory timeline, snapshot gallery, celebrants, countdown,
 * fun facts, and the interactive Wishes Wall + Memory Capsule.
 * Rendering delegated to the frontend template `registry` by `id`.
 */
const BIRTHDAY_PREMIUM_TEMPLATE: ApiTemplate = {
  id: 'birthday-premium',
  slug: 'birthday-premium',
  name: 'Birthday Premium',
  occasionType: 'BIRTHDAY',
  previewImageUrl: null,
  isPremium: true,
  isActive: true,
  description:
    'Premium story-driven birthday invitation: full-screen hero, memory timeline, snapshot gallery, celebrants, activity schedule, countdown, fun facts, interactive wishes wall, and memory capsule.',
  tags: ['birthday', 'blue', 'animated', 'multi-section', 'celebration', 'premium', 'story', 'interactive'],
  capabilities: { experienceRenderer: 'birthday-premium' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const EXP_BIRTHDAY_SUPERHERO_TEMPLATE: ApiTemplate = {
  id: 'exp-birthday-superhero-v1',
  slug: 'exp-birthday-superhero-v1',
  name: 'Superhero Avengers Assembly',
  occasionType: 'BIRTHDAY',
  previewImageUrl: '/assets/birthday/cyberpunk_luxury_cake.jpg',
  isPremium: true,
  isActive: true,
  description:
    'Action-packed birthday experience: Stark Observatory, Cyberpunk Lounge, Iron Man vs Thanos game, and 360 panorama.',
  tags: ['cinematic', 'birthday', 'superhero', 'avengers', 'ironman', 'marvel', 'kids', 'premium', 'scroll-experience', 'cyberpunk', 'game'],
  capabilities: { experience: true, aiNative: true, stylePack: 'midnight_glow', supportsRSVP: true, experienceRenderer: 'birthday-superhero', aiImageGenDisabled: true },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const EXP_BIRTHDAY_DARK_KNIGHT_TEMPLATE: ApiTemplate = {
  id: 'exp-birthday-dark-knight-v1',
  slug: 'exp-birthday-dark-knight-v1',
  name: 'DC Dark Knight Cyber',
  occasionType: 'BIRTHDAY',
  previewImageUrl: '/assets/birthday/batman_joker_birthday.jpg',
  isPremium: true,
  isActive: true,
  description:
    'Sleek Gotham cyber-vault birthday experience: Batcave command center, Bat-Signal spotlight beam, and Joker Cake Surprise.',
  tags: ['cinematic', 'birthday', 'dc', 'batman', 'dark-knight', 'gotham', 'joker', 'teens', 'premium', 'scroll-experience'],
  capabilities: { experience: true, aiNative: true, stylePack: 'modern_noir', supportsRSVP: true, experienceRenderer: 'birthday-dark-knight', aiImageGenDisabled: true },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const EXP_BIRTHDAY_WONDERLAND_TEMPLATE: ApiTemplate = {
  id: 'exp-birthday-wonderland-v1',
  slug: 'exp-birthday-wonderland-v1',
  // Matches the backend seed's name (scripts/seed-templates.ts) — that row already
  // avoids the "Barbie" trademark; this hardcoded fallback previously didn't, so the
  // template showed a different name depending on whether it loaded from the live API
  // or this offline fallback.
  name: 'Magical Fairy & Balloon Castle',
  occasionType: 'BIRTHDAY',
  previewImageUrl: '/assets/birthday/barbie_princess_castle.jpg',
  isPremium: true,
  isActive: true,
  description:
    'Magical fairy garden & balloon castle birthday experience: princess tea party, magic wands, and living petal showers.',
  tags: ['cinematic', 'birthday', 'wonderland', 'princess', 'fairy', 'balloons', 'kids', 'premium', 'scroll-experience'],
  capabilities: { experience: true, aiNative: true, stylePack: 'pastel_dream', supportsRSVP: true, experienceRenderer: 'birthday-wonderland', aiImageGenDisabled: true },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

/**
 * Birthday Minimalist AI — clean, elegant experience powered by the `minimal` style pack.
 * Restored: these 3 were previously seeded as exp-birthday-*-v1 before the migration cleanup.
 */
const EXP_BIRTHDAY_MINIMALIST_AI_TEMPLATE: ApiTemplate = {
  id: 'exp-birthday-minimal-v1',
  slug: 'exp-birthday-minimal-v1',
  name: 'Birthday Minimalist',
  occasionType: 'BIRTHDAY',
  previewImageUrl: '/assets/marathi/backdrop.jpg',
  isPremium: true,
  isActive: false,
  description:
    'A clean and elegant AI-generated birthday experience with soft linen tones, cinematic parallax, and smooth scroll interactions.',
  tags: ['ai-native', 'experience', 'minimal', 'birthday', 'elegant', 'clean', 'modern', 'premium', 'scroll-experience'],
  capabilities: { experience: true, aiNative: true, stylePack: 'minimal', supportsRSVP: true, experienceRenderer: 'experience-generic' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

/**
 * Birthday Premium Thematic AI — rich, cinematic experience with premium_thematic style pack.
 */
const EXP_BIRTHDAY_PREMIUM_THEMATIC_AI_TEMPLATE: ApiTemplate = {
  id: 'exp-birthday-premium-v1',
  slug: 'exp-birthday-premium-v1',
  name: 'Birthday Premium Thematic',
  occasionType: 'BIRTHDAY',
  previewImageUrl: '/assets/birthday/cyberpunk_luxury_cake.jpg',
  isPremium: true,
  isActive: false,
  description:
    'A rich, cinematic AI-generated birthday experience with gold and crimson premium thematic styling, immersive scroll effects, and celebration moments.',
  tags: ['ai-native', 'experience', 'premium_thematic', 'birthday', 'cinematic', 'royal', 'premium', 'scroll-experience', 'thematic'],
  capabilities: { experience: true, aiNative: true, stylePack: 'premium_thematic', supportsRSVP: true, experienceRenderer: 'experience-generic' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

/**
 * Birthday Modern Vibrant AI — high-energy, vibrant experience with modern_vibrant style pack.
 */
const EXP_BIRTHDAY_MODERN_VIBRANT_AI_TEMPLATE: ApiTemplate = {
  id: 'exp-birthday-vibrant-v1',
  slug: 'exp-birthday-vibrant-v1',
  name: 'Birthday Modern Vibrant',
  occasionType: 'BIRTHDAY',
  previewImageUrl: '/assets/birthday/cyberpunk_skyline_fireworks.jpg',
  isPremium: true,
  isActive: false,
  description:
    'A high-energy, vibrant AI-generated birthday experience with bright coral and gold tones, dynamic particles, and energetic scroll-driven celebration moments.',
  tags: ['ai-native', 'experience', 'modern_vibrant', 'birthday', 'vibrant', 'energetic', 'colorful', 'premium', 'scroll-experience'],
  capabilities: { experience: true, aiNative: true, stylePack: 'modern_vibrant', supportsRSVP: true, experienceRenderer: 'experience-generic' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

const BIRTHDAY_NEON_BASH_TEMPLATE: ApiTemplate = {
  id: 'birthday-neon-bash',
  slug: 'birthday-neon-bash',
  name: 'Birthday Neon Bash',
  occasionType: 'BIRTHDAY',
  previewImageUrl: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=720&q=80',
  isPremium: true,
  isActive: true,
  description:
    'Ultra-modern interactive birthday invitation featuring 3D perspective transforms, real-time particle animation, neon color scheme (cyan/pink/purple), countdown timer, interactive memory carousel, guest wishes board, and RSVP.',
  tags: ['birthday', 'neon', 'modern', 'interactive', '3d-transforms', 'particle-effects'],
  capabilities: { experienceRenderer: 'birthday-neon-bash' },
  version: '1.0.0',
  renderMode: 'experience',
  layoutSchema: null,
  dataSchema: null,
  themeConfig: null,
};

function makeSampleData(tpl: ApiTemplate): ApiInvitation {
  const base = {
    id: `preview-${tpl.id}`,
    publicSlug: null,
    templateId: tpl.id,
    occasionType: tpl.occasionType,
    hostedUrl: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  // Birthday templates get a birthday-shaped sample. status is DRAFT so the
  // interactive sections render their sample content locally without firing
  // API calls against the fake preview id.
  if (tpl.occasionType === 'BIRTHDAY') {
    return {
      ...base,
      status: 'DRAFT',
      eventName: 'Aarav',
      eventDate: new Date(Date.now() + 12 * 24 * 60 * 60 * 1000).toISOString(),
      eventVenue: 'Sunshine Banquet Hall, Mumbai',
      hostName: 'The Sharma Family',
      customMessage: "We can't wait to celebrate this special day with you!",
      rsvpLink: '#',
      mediaUrls: [],
      customData: {
        birthday: {
          birthdayPersonName: 'Aarav',
          ageTurning: '7',
          celebrationTagline: 'Celebrating a Day Filled with Joy, Laughter & Beautiful Memories',
          hostNames: 'Mom & Dad',
          eventTime: '5:30 PM',
          venueName: 'Sunshine Banquet Hall',
          venueAddress: 'Sunshine Banquet Hall, Andheri West, Mumbai',
          snapshots: [
            { imageUrl: 'https://images.unsplash.com/photo-1464349095431-e9a21285b5f3?auto=format&fit=crop&w=600&q=80', caption: 'First Smile' },
            { imageUrl: 'https://images.unsplash.com/photo-1543248939-4296e1fea89b?auto=format&fit=crop&w=600&q=80', caption: 'First Steps' },
          ],
          celebrants: [
            { name: 'Aarav', relation: 'Birthday Star' },
            { name: 'Riya', relation: 'Big Sister' },
          ],
          activities: [
            { time: '5:30 PM', title: 'Guest Arrival', emoji: '🎈' },
            { time: '6:30 PM', title: 'Games & Fun', emoji: '🎮' },
            { time: '7:00 PM', title: 'Cake Cutting', emoji: '🎂' },
          ],
          funFacts: [
            { label: 'Favourite Food', value: 'Pizza 🍕' },
            { label: 'Favourite Colour', value: 'Blue 💙' },
          ],
        },
      },
    } as ApiInvitation;
  }

  return {
    id: `preview-${tpl.id}`,
    publicSlug: null,
    templateId: tpl.id,
    occasionType: tpl.occasionType,
    status: 'PUBLISHED',
    hostedUrl: null,
    eventName: 'Your Event Name',
    eventDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    eventVenue: 'The Grand Venue, Mumbai',
    hostName: 'Your Name',
    customMessage: 'A heartfelt message to your guests goes here.',
    rsvpLink: '#',
    mediaUrls: [],
    customData: {
      wedding: {
        groom: {
          fullName: 'Rahul Sharma',
          familyPositionText: 'Second Son of Mr. & Mrs. Amit Sharma',
          fatherName: 'Amit Sharma',
          motherName: 'Neelam Sharma',
          homeAddress: 'Bandra West, Mumbai',
        },
        bride: {
          fullName: 'Priya Mehta',
          familyPositionText: 'Eldest Daughter of Mr. & Mrs. Sanjay Mehta',
          fatherName: 'Sanjay Mehta',
          motherName: 'Kavita Mehta',
          homeAddress: 'Juhu, Mumbai',
        },
        weddingEvents: [
          {
            title: 'Haldi',
            date: new Date(Date.now() + 27 * 24 * 60 * 60 * 1000).toISOString(),
            startTime: '10:00 AM',
            venue: 'Sharma Residence, Bandra West',
            description: 'Turmeric & sunshine. Wear yellow!',
          },
          {
            title: 'Mehendi',
            date: new Date(Date.now() + 28 * 24 * 60 * 60 * 1000).toISOString(),
            startTime: '05:00 PM',
            venue: 'Courtyard Garden, Mumbai',
            description: 'An evening of henna, music, and family laughter.',
          },
          {
            title: 'Sangeet',
            date: new Date(Date.now() + 28 * 24 * 60 * 60 * 1000).toISOString(),
            startTime: '08:00 PM',
            venue: 'The Grand Ballroom, Mumbai',
            description: 'Dance, music, and celebration with loved ones.',
          },
          {
            title: 'Wedding Ceremony',
            date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
            startTime: '11:00 AM',
            venue: 'The Grand Venue, Mumbai',
            description: 'The ceremony, blessings, and sacred rituals.',
          },
          {
            title: 'Reception',
            date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
            startTime: '07:00 PM',
            venue: 'The Grand Venue, Mumbai',
            description: 'Dinner and celebrations for all.',
          },
        ],
        familyTree: [
          { id: 'gg-p', name: 'Late Shri Ramchandra Sharma', relationship: "Groom's Grandfather" },
          { id: 'gg-m', name: 'Smt. Savitri Sharma', relationship: "Groom's Grandmother", parentId: 'gg-p' },
          { id: 'gp', name: 'Mr. Amit Sharma', relationship: "Groom's Father", parentId: 'gg-p' },
          { id: 'groom-node', name: 'Rahul Sharma', relationship: 'Groom', parentId: 'gp' },
          { id: 'bg-p', name: 'Late Shri Mohan Mehta', relationship: "Bride's Grandfather" },
          { id: 'bp', name: 'Mr. Sanjay Mehta', relationship: "Bride's Father", parentId: 'bg-p' },
          { id: 'bride-node', name: 'Priya Mehta', relationship: 'Bride', parentId: 'bp' },
        ],
        familyInvitations: [
          { id: 'fi-1', name: 'Ramesh Sharma', relation: 'Paternal Uncle', message: 'Please join us and bless the couple on this auspicious occasion.' },
          { id: 'fi-2', name: 'Sunita Mehta', relation: 'Maternal Aunt', message: 'Your presence will make this day even more beautiful for our family.' },
          { id: 'fi-3', name: 'Rohan Sharma', relation: 'Elder Brother', message: 'Come celebrate the beginning of a beautiful journey with us!' },
        ],
        showFamilyInvitations: true,
        showOrganizers: true,
        organizers: [
          { id: 'org-1', name: 'Mr. Rajesh Sharma', designation: 'Main Organizer' },
          { id: 'org-2', name: 'Mrs. Sunita Sharma', designation: 'Co-Organizer' },
          { id: 'org-3', name: 'Mr. Amit Verma', designation: 'Coordinator' },
        ],
        showFamilyChildren: true,
        familyChildrenTitle: 'Our Little Stars',
        familyChildren: [
          { id: 'child-1', name: 'Aryan' },
          { id: 'child-2', name: 'Kiara' },
          { id: 'child-3', name: 'Aarav' },
          { id: 'child-4', name: 'Anaya' },
        ],
      },
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

export function StepTemplate({ onNext, onBack, data, updateData }: StepProps) {
  const [templates, setTemplates] = useState<ApiTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [previewing, setPreviewing] = useState<ApiTemplate | null>(null);
  const [pendingSwitch, setPendingSwitch] = useState<{
    target: ApiTemplate;
    report: SwitchReport;
    nextCustomData: Record<string, unknown>;
    proceed: () => void;
  } | null>(null);

  useEffect(() => {
    setLoading(true);
    setError('');
    getTemplates(data.occasionType, 150)
      .then((apiTemplates) => {
        let combinedTemplates = [...apiTemplates];

        if (data.occasionType === 'WEDDING') {
          const hardcoded = [
            VIDEO_ROYAL_01_TEMPLATE,
            VIDEO_KIRTAN_DIVINE_TEMPLATE,
            VIDEO_RADHA_KRISHNA_TEMPLATE,
            EXP_GSAP_RADHA_KRISHNA_TEMPLATE,
            EXP_GSAP_SHIVA_PARVATI_TEMPLATE,
            EXP_CINEMATIC_ROYAL_TEMPLATE,
            EXP_CINEMATIC_MINIMAL_TEMPLATE,
            EXP_CINEMATIC_FANTASY_TEMPLATE,
            ROYAL_GANESH_INTERACTIVE_TEMPLATE,
            CINEMATIC_WEDDING_TEMPLATE,
            DEV21_WEDDING_TEMPLATE,
            LAGNA_PATRIKA_INTERACTIVE_TEMPLATE,
            STITCH_IMMERSIVE_WEDDING_TEMPLATE,
            MARATHI_WEDDING_CLASSIC_TEMPLATE,
            MARATHI_WEDDING_INTERACTIVE_TEMPLATE,
            TRADITIONAL_WEDDING_INTERACTIVE_TEMPLATE,
            SOUTH_INDIAN_TRADITION_TEMPLATE,
            NIKAH_ELEGANT_TEMPLATE,
            EXP_WEDDING_RAJASTHANI_ROYAL_TEMPLATE,
          ];
          const dbSlugs = new Set(apiTemplates.map((t) => t.slug));
          combinedTemplates = [...combinedTemplates, ...hardcoded.filter((t) => !dbSlugs.has(t.slug))];
        } else if (data.occasionType === 'ENGAGEMENT') {
          const hardcoded = [ROYAL_ENGAGEMENT_TEMPLATE, MODERN_FUSION_TEMPLATE];
          const dbSlugs = new Set(apiTemplates.map((t) => t.slug));
          combinedTemplates = [...combinedTemplates, ...hardcoded.filter((t) => !dbSlugs.has(t.slug))];
        } else if (data.occasionType === 'BIRTHDAY') {
          const hardcoded = [
            EXP_BIRTHDAY_SUPERHERO_TEMPLATE,
            EXP_BIRTHDAY_DARK_KNIGHT_TEMPLATE,
            EXP_BIRTHDAY_WONDERLAND_TEMPLATE,
            EXP_BIRTHDAY_MINIMALIST_AI_TEMPLATE,
            EXP_BIRTHDAY_PREMIUM_THEMATIC_AI_TEMPLATE,
            EXP_BIRTHDAY_MODERN_VIBRANT_AI_TEMPLATE,
            BIRTHDAY_NEON_BASH_TEMPLATE,
            BIRTHDAY_PREMIUM_TEMPLATE,
            BIRTHDAY_CELEBRATION_TEMPLATE,
          ];
          const dbSlugs = new Set(apiTemplates.map((t) => t.slug));
          combinedTemplates = [...combinedTemplates, ...hardcoded.filter((t) => !dbSlugs.has(t.slug))];
        }

        const activeTemplates = combinedTemplates.filter((t) => t.isActive !== false);
        setTemplates(activeTemplates);
        if (!data.templateId && activeTemplates.length > 0) {
          const preferredId = data.occasionType === 'WEDDING'
            ? CINEMATIC_WEDDING_TEMPLATE.id
            : data.occasionType === 'BIRTHDAY'
              ? BIRTHDAY_CELEBRATION_TEMPLATE.id
              : undefined;
          const defaultTemplate = activeTemplates.find(template => template.id === preferredId)
            ?? activeTemplates[0];
          updateData({ templateId: defaultTemplate.id });
        }
      })
      .catch((err) => {
        console.warn('Backend templates API failed, using resilient hardcoded catalog fallback:', err);
        let hardcoded: ApiTemplate[] = [];
        if (data.occasionType === 'WEDDING') {
          hardcoded = [
            VIDEO_ROYAL_01_TEMPLATE,
            VIDEO_KIRTAN_DIVINE_TEMPLATE,
            EXP_CINEMATIC_ROYAL_TEMPLATE,
            EXP_CINEMATIC_MINIMAL_TEMPLATE,
            EXP_CINEMATIC_FANTASY_TEMPLATE,
            ROYAL_GANESH_INTERACTIVE_TEMPLATE,
            CINEMATIC_WEDDING_TEMPLATE,
            DEV21_WEDDING_TEMPLATE,
            LAGNA_PATRIKA_INTERACTIVE_TEMPLATE,
            STITCH_IMMERSIVE_WEDDING_TEMPLATE,
            MARATHI_WEDDING_CLASSIC_TEMPLATE,
            MARATHI_WEDDING_INTERACTIVE_TEMPLATE,
            TRADITIONAL_WEDDING_INTERACTIVE_TEMPLATE,
            SOUTH_INDIAN_TRADITION_TEMPLATE,
            NIKAH_ELEGANT_TEMPLATE,
            EXP_WEDDING_RAJASTHANI_ROYAL_TEMPLATE,
          ];
        } else if (data.occasionType === 'ENGAGEMENT') {
          hardcoded = [ROYAL_ENGAGEMENT_TEMPLATE, MODERN_FUSION_TEMPLATE];
        } else if (data.occasionType === 'BIRTHDAY') {
          hardcoded = [
            EXP_BIRTHDAY_SUPERHERO_TEMPLATE,
            EXP_BIRTHDAY_DARK_KNIGHT_TEMPLATE,
            EXP_BIRTHDAY_WONDERLAND_TEMPLATE,
            EXP_BIRTHDAY_PREMIUM_THEMATIC_AI_TEMPLATE,
            EXP_BIRTHDAY_MODERN_VIBRANT_AI_TEMPLATE,
            BIRTHDAY_NEON_BASH_TEMPLATE,
          ];
        } else {
          hardcoded = [VIDEO_ROYAL_01_TEMPLATE, VIDEO_KIRTAN_DIVINE_TEMPLATE];
        }
        const activeTemplates = hardcoded.filter((t) => t.isActive !== false);
        setTemplates(activeTemplates);
        if (!data.templateId && activeTemplates.length > 0) {
          updateData({ templateId: activeTemplates[0].id });
        }
      })
      .finally(() => setLoading(false));
  }, [data.occasionType]);

  useEffect(() => {
    trackEvent('TEMPLATE_VIEW', 'wizard');
  }, []);

  const selected = data.templateId;

  /** Per-template style tag — no generic rule, so it's a lookup + id-suffix fallback. */
  function resolveStyle(tpl: ApiTemplate): string | undefined {
    let style = EXPERIENCE_STYLE_BY_TEMPLATE_ID[tpl.id];
    if (!style) {
      if (tpl.id.includes('-minimal') || tpl.id.endsWith('-minimal-v1')) {
        style = 'minimal';
      } else if (tpl.id.includes('-premium_thematic') || tpl.id.endsWith('-premium_thematic-v1')) {
        style = 'premium_thematic';
      } else if (tpl.id.includes('-modern_vibrant') || tpl.id.endsWith('-modern_vibrant-v1')) {
        style = 'modern_vibrant';
      }
    }
    return style;
  }

  /**
   * Record whether this template is driven by the Type-A DSL pipeline — the honest
   * signal is the template's own `capabilities.aiNative` flag, never the id string.
   * (A past fix string-matched `exp-cinematic-`, which silently excluded every other
   * AI-native template — birthday flagships, Rajasthani — from ever building a DSL.
   * Do not reintroduce that pattern here.)
   */
  function resolveIsAiNative(tpl: ApiTemplate): boolean {
    const caps = (tpl.capabilities ?? {}) as { aiNative?: boolean };
    return caps.aiNative === true;
  }

  /** First-ever pick, or re-clicking the already-selected template — nothing to reconcile. */
  function applyFreshSelection(tpl: ApiTemplate) {
    const style = resolveStyle(tpl);
    const isAiNative = resolveIsAiNative(tpl);
    updateData({
      templateId: tpl.id,
      ...(style || isAiNative ? {
        customData: {
          ...data.customData,
          experience: {
            ...(data.customData?.experience as object | undefined),
            ...(style ? { template: style } : {}),
            aiNative: isAiNative,
            occasion: tpl.occasionType.toLowerCase(),
          },
        } as any,
      } : {}),
    });
  }

  /**
   * Pick `tpl` as the wizard's template. `proceed` (usually onNext, or closing the
   * preview modal + onNext) only runs immediately for a fresh pick — a genuine switch
   * away from an already-chosen template instead opens the impact dialog, and
   * `proceed` runs from confirmPendingSwitch() once the user confirms.
   */
  /**
   * Whether there's anything a switch could actually lose. The wizard
   * pre-selects a default templateId on mount (see the useEffect above) so
   * the picker isn't empty — that default is not a user decision, and a
   * user's very first click must not be treated as a "switch" away from it.
   * Content-based rather than a mount/ref flag: this component remounts on
   * every AnimatePresence step transition (see Wizard.tsx), so any local
   * "has the user confirmed yet" flag would reset the moment they navigate
   * away and back, wrongly skipping the dialog on a real second switch.
   */
  function hasMeaningfulContent(d: typeof data): boolean {
    if (d.eventName?.trim() || d.hostName?.trim() || d.eventVenue?.trim() || d.customMessage?.trim()) {
      return true;
    }
    if (d.mediaUrls && d.mediaUrls.length > 0) return true;
    const cd = d.customData as Record<string, unknown> | undefined;
    if (cd && Object.keys(cd).some((k) => k !== 'experience' && k !== '_parked')) return true;
    return false;
  }

  function requestTemplateChange(tpl: ApiTemplate, proceed: () => void) {
    const previousTemplateId = data.templateId;
    if (!previousTemplateId || previousTemplateId === tpl.id || !hasMeaningfulContent(data)) {
      // A swap with nothing typed yet still counts as "changed my mind" — it is the friction we measure.
      trackTemplateChange(tpl.id);
      applyFreshSelection(tpl);
      proceed();
      return;
    }

    const fromTemplate = templates.find((t) => t.id === previousTemplateId);
    const { customData: nextCustomData, report } = switchTemplate({
      customData: data.customData as Record<string, unknown> | undefined,
      fromTemplateId: previousTemplateId,
      toTemplateId: tpl.id,
      fromSchemaRaw: fromTemplate?.dataSchema,
      toSchemaRaw: tpl.dataSchema,
      occasionType: data.occasionType,
      isAiNative: resolveIsAiNative(tpl),
      style: resolveStyle(tpl),
    });

    setPendingSwitch({ target: tpl, report, nextCustomData, proceed });
  }

  function confirmPendingSwitch() {
    if (!pendingSwitch) return;
    const { target, nextCustomData, proceed } = pendingSwitch;
    trackTemplateChange(target.id);
    updateData({ templateId: target.id, customData: nextCustomData as any });
    setPendingSwitch(null);
    proceed();
  }

  function cancelPendingSwitch() {
    if (pendingSwitch && data.templateId) {
      trackEvent('TEMPLATE_CHANGE_CANCELLED', 'wizard', pendingSwitch.target.id, undefined, {
        from_template_id: data.templateId,
        to_template_id: pendingSwitch.target.id,
      });
    }
    setPendingSwitch(null);
  }

  return (
    <div className="w-full flex flex-col items-center">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-brand-deep mb-4">Choose a Template</h1>
        <p className="text-on-surface-variant font-medium max-w-2xl">
          Select a starting point for your invitation. Click Preview to see a live render.
        </p>
      </div>

      {loading && (
        <div className="flex justify-center py-16">
          <div className="w-10 h-10 rounded-full border-4 border-primary border-t-transparent animate-spin" />
        </div>
      )}

      {!loading && error && (
        <p className="text-status-failed font-medium mb-8">{error}</p>
      )}

      {!loading && !error && templates.length === 0 && (
        <div className="text-center py-16 text-on-surface-variant font-medium">
          No templates available for this occasion yet. More coming soon!
        </div>
      )}

      {!loading && !error && templates.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full mb-16">
          {templates.map((tpl) => {
            const isSelected = selected === tpl.id;
            return (
              <motion.div
                layoutId={tpl.id}
                key={tpl.id}
                onClick={() => {
                  trackEvent('TEMPLATE_SELECTED', 'wizard', tpl.id, undefined, { template_id: tpl.id });
                  requestTemplateChange(tpl, onNext);
                }}
                className={cn(
                  'group relative rounded-3xl border-2 overflow-hidden flex flex-col transition-all cursor-pointer',
                  isSelected
                    ? 'border-primary bg-surface shadow-2xl ring-4 ring-primary/10'
                    : 'border-surface-low bg-surface shadow-sm hover:shadow-xl hover:border-outline-variant',
                )}
              >
                {tpl.isPremium && (
                  <div className="absolute top-4 left-4 z-20 bg-amber-500 text-white text-[10px] font-black uppercase px-2 py-0.5 rounded-full">
                    Premium
                  </div>
                )}
                {isSelected && (
                  <div className="absolute top-4 right-4 z-20 bg-primary text-white rounded-full p-1 shadow-lg">
                    <Icons.CheckCircle size={18} />
                  </div>
                )}
                <div className="relative w-full aspect-[4/5] bg-surface-low overflow-hidden">
                  {tpl.occasionType === 'WEDDING' || tpl.previewImageUrl ? (
                    <TemplateCardPreview template={tpl} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-on-surface-variant/20">
                      <Icons.Calendar size={64} />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-brand-deep/20 backdrop-blur-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <Button
                      variant="secondary"
                      size="sm"
                      className="font-bold flex items-center gap-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPreviewing(tpl);
                      }}
                    >
                      <Icons.Search size={14} /> Preview
                    </Button>
                  </div>
                </div>
                <div className="p-6 flex flex-col gap-1">
                  <h3 className="text-lg font-bold text-on-surface">{tpl.name}</h3>
                  <p className="text-xs text-on-surface-variant capitalize">
                    {tpl.occasionType.replace('_', ' ').toLowerCase()}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      <div className="w-full flex items-center pt-8 border-t border-outline-variant/10">
        <Button variant="outline" onClick={onBack} className="flex items-center gap-2">
          <Icons.ArrowLeft size={18} /> Back
        </Button>
      </div>

      {/* Live preview modal */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {previewing && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-[#1A202C] overflow-y-auto"
              onClick={() => setPreviewing(null)}
            >
              <div className="relative w-full min-h-screen" onClick={(e) => e.stopPropagation()}>
                {/* Floating controls in a glassmorphic bar */}
                <div className="fixed top-4 left-4 z-[60] flex gap-3">
                  <Button
                    variant="secondary"
                    className="bg-black/60 hover:bg-black/80 text-white border-none rounded-full px-5 py-2.5 backdrop-blur-md shadow-lg flex items-center gap-2"
                    onClick={() => setPreviewing(null)}
                  >
                    <Icons.ArrowLeft size={16} /> Back to Wizard
                  </Button>
                  <Button
                    className="bg-[#D4AF37] hover:bg-[#B8962E] text-[#1A202C] rounded-full px-6 py-2.5 font-bold shadow-lg"
                    onClick={() => {
                      const tpl = previewing;
                      // Close the preview first — if this turns out to be a genuine
                      // switch, the impact dialog should appear over the picker, not
                      // stacked on top of the full-bleed preview overlay.
                      setPreviewing(null);
                      requestTemplateChange(tpl, onNext);
                    }}
                  >
                    Select This Template
                  </Button>
                </div>

                {/* The full view template rendering */}
                <div className="w-full min-h-screen">
                  {registry.has(previewing.id) ? (
                    <TemplateRenderer
                      templateId={previewing.id}
                      data={makeSampleData(previewing)}
                    />
                  ) : previewing.renderMode === 'json-renderer' && previewing.layoutSchema ? (
                    <JsonTemplateRenderer
                      schema={previewing.layoutSchema}
                      data={makeSampleData(previewing)}
                      theme={previewing.themeConfig}
                      mode="preview"
                    />
                  ) : previewing.renderMode === 'experience' || previewing.id.startsWith('exp-') ? (
                    <SceneExperienceRenderer manifest={resolveExperienceManifest(previewing, makeSampleData(previewing))} />
                  ) : previewing.occasionType === 'WEDDING' ? (
                    <CinematicWeddingExperience data={makeSampleData(previewing)} />
                  ) : (
                    <SceneExperienceRenderer manifest={resolveExperienceManifest(previewing, makeSampleData(previewing))} />
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      <SwitchTemplateDialog
        isOpen={pendingSwitch !== null}
        targetTemplateName={pendingSwitch?.target.name ?? ''}
        report={pendingSwitch?.report ?? { carriedOver: [], parked: [], missing: [] }}
        onConfirm={confirmPendingSwitch}
        onCancel={cancelPendingSwitch}
      />
    </div>
  );
}
